import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Factory, Plus, ArrowUp, ArrowDown, Lock, Unlock, Printer, Pencil, Trash2, AlertTriangle, Pause, Play } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const emptyOrd = { macchine_codici: [], articolo_id: "", articolo_codice: "", kg_target: "", materiale: "", note: "",
                   stampa_colori_override: "", additivi_override: "", cliente_override: "" };

export default function EstrusioneOrdini() {
    const { user, can } = useAuth();
    const nav = useNavigate();
    const canEdit = user?.role === "admin" || can("ord_estr.edit");
    const canClose = user?.role === "admin" || can("ord_estr.chiudi");
    const [macc, setMacc] = useState([]);
    const [artc, setArtc] = useState([]);
    const [ord, setOrd] = useState([]);
    const [stato, setStato] = useState("aperto");
    const [openDlg, setOpenDlg] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyOrd);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [m, a, o] = await Promise.all([
                api.get("/macchine-estrusione"),
                api.get("/articoli-estrusione"),
                // Ufficio vede TUTTI gli ordini (parcheggiati inclusi) per gestirli
                api.get(`/ordini-estrusione?stato=${stato}&parcheggiato=all`),
            ]);
            setMacc(m.data.filter(x => x.attiva));
            setArtc(a.data);
            setOrd(o.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [stato]);

    const parcheggiati = useMemo(() => ord.filter(o => o.parcheggiato), [ord]);
    const byMacc = useMemo(() => {
        const map = {};
        for (const m of macc) map[m.codice] = [];
        map.PARCHEGGIO = [];  // 6ª "macchina virtuale" per gli ordini sospesi
        for (const o of ord) {
            if (o.parcheggiato) { map.PARCHEGGIO.push(o); continue; }
            const macs = (o.macchine_codici && o.macchine_codici.length) ? o.macchine_codici : (o.macchina_codice ? [o.macchina_codice] : []);
            for (const mc of macs) {
                if (!map[mc]) map[mc] = [];
                map[mc].push(o);
            }
        }
        return map;
    }, [ord, macc]);
    // Elenco cards da renderizzare: 5 estrusori + Parcheggio in coda
    const macchineDaMostrare = useMemo(() => [
        ...macc,
        { codice: "PARCHEGGIO", descrizione: "Ordini sospesi · invisibili agli operatori", virtual: true }
    ], [macc]);

    const selArt = useMemo(() => artc.find(a => a.id === form.articolo_id), [artc, form.articolo_id]);

    // Quando cambio articolo, precompila override con i valori dell'articolo (in creazione)
    useEffect(() => {
        if (selArt && !editId) {
            setForm(f => ({ ...f,
                articolo_codice: selArt.codice,
                stampa_colori_override: selArt.stampa_colori ?? "",
                additivi_override: selArt.additivi ?? "",
                cliente_override: selArt.cliente_nome ?? "" }));
        }
    }, [selArt?.id, editId]);

    const openNew = () => { setEditId(null); setForm({...emptyOrd, macchine_codici: macc[0] ? [macc[0].codice] : []}); setOpenDlg(true); };
    const openEdit = (o) => {
        setEditId(o.id);
        const artObj = artc.find(a => a.id === o.articolo_id);
        const macs = (o.macchine_codici && o.macchine_codici.length) ? [...o.macchine_codici] : (o.macchina_codice ? [o.macchina_codice] : []);
        setForm({ macchine_codici: macs, articolo_id: o.articolo_id,
                  articolo_codice: artObj?.codice || "",
                  kg_target: o.kg_target ?? "", materiale: o.materiale || "", note: o.note || "",
                  stampa_colori_override: o.stampa_colori_override ?? "",
                  additivi_override: o.additivi_override ?? "",
                  cliente_override: o.cliente_override ?? "" });
        setOpenDlg(true);
    };

    const toggleMacc = (codice) => {
        setForm(f => {
            const cur = f.macchine_codici || [];
            const next = cur.includes(codice) ? cur.filter(x => x !== codice) : [...cur, codice];
            return { ...f, macchine_codici: next };
        });
    };

    const save = async () => {
        if (!form.macchine_codici?.length || !form.articolo_id || !form.kg_target) {
            toast.error("Almeno una macchina, articolo e kg target sono obbligatori"); return;
        }
        let scoOverride = form.stampa_colori_override;
        let addOverride = form.additivi_override;
        let cliOverride = form.cliente_override;
        const artStampa = selArt?.stampa_colori || "";
        const artAdd = selArt?.additivi || "";
        const artCli = selArt?.cliente_nome || "";
        const stampaChanged = (scoOverride ?? "").trim() !== artStampa.trim();
        const addChanged = (addOverride ?? "").trim() !== artAdd.trim();
        const cliChanged = (cliOverride ?? "").trim() !== artCli.trim();
        let propagate = false;
        if (selArt && (stampaChanged || addChanged || cliChanged)) {
            const parts = [];
            if (cliChanged) parts.push(`• Cliente: "${artCli || "vuoto"}" → "${cliOverride || "vuoto"}"`);
            if (stampaChanged) parts.push(`• Stampa n° colori: "${artStampa || "vuoto"}" → "${scoOverride || "vuoto"}"`);
            if (addChanged) parts.push(`• Additivi: "${artAdd || "vuoto"}" → "${addOverride || "vuoto"}"`);
            propagate = window.confirm(
                `Hai modificato:\n${parts.join("\n")}\n\n` +
                `OK  = Applica PER SEMPRE sull'articolo (${selArt.codice}), sarà il nuovo default.\n` +
                `Annulla = Solo per QUESTO ordine (override sull'ordine, articolo invariato).`
            );
        }
        const payload = {
            macchine_codici: form.macchine_codici,
            articolo_id: form.articolo_id,
            kg_target: parseFloat(form.kg_target) || 0,
            materiale: form.materiale?.trim() || null,
            note: form.note?.trim() || null,
            stampa_colori_override: propagate ? null : (stampaChanged ? (scoOverride?.trim() || "") : null),
            additivi_override: propagate ? null : (addChanged ? (addOverride?.trim() || "") : null),
            cliente_override: propagate ? null : (cliChanged ? (cliOverride?.trim() || "") : null),
        };
        try {
            if (propagate && selArt) {
                const artPayload = {
                    codice: selArt.codice,
                    larghezza_cm: selArt.larghezza_cm, spessore_my: selArt.spessore_my,
                    colore_nome: selArt.colore_nome, tipo_estrusione: selArt.tipo_estrusione,
                    cliente_nome: cliChanged ? (cliOverride?.trim() || null) : selArt.cliente_nome,
                    trattamento: selArt.trattamento, note: selArt.note,
                    stampa_colori: stampaChanged ? (scoOverride?.trim() || null) : selArt.stampa_colori,
                    additivi: addChanged ? (addOverride?.trim() || null) : selArt.additivi,
                };
                await api.put(`/articoli-estrusione/${selArt.id}`, artPayload);
                toast.success("Articolo aggiornato (default nuovo)");
            }
            if (editId) { await api.put(`/ordini-estrusione/${editId}`, payload); toast.success("Ordine aggiornato"); }
            else { await api.post("/ordini-estrusione", payload); toast.success("Ordine creato"); }
            setOpenDlg(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const del = async (o) => {
        if (!window.confirm(`Eliminare ordine ${o.numero}?`)) return;
        try { await api.delete(`/ordini-estrusione/${o.id}`); toast.success("Eliminato"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const moveInMacc = async (o, dir, macCodice) => {
        // Riordino nella coda della macchina cliccata (usa la macchina passata, non `o.macchina_codice`)
        const key = macCodice || o.macchina_codice;
        const q = byMacc[key] || [];
        const idx = q.findIndex(x => x.id === o.id);
        const j = dir === "up" ? idx - 1 : idx + 1;
        if (idx < 0 || j < 0 || j >= q.length) return;
        const newList = [...q];
        [newList[idx], newList[j]] = [newList[j], newList[idx]];
        try {
            await api.post("/ordini-estrusione/reorder", { ids_in_order: newList.map(x => x.id) });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const moveToMacc = async (o, newMacc) => {
        if (!newMacc) return;
        // Sovrascrive TUTTE le macchine assegnate con la sola scelta (comportamento "solo su X")
        if ((o.macchine_codici || []).length > 1) {
            if (!window.confirm(`L'ordine ${o.numero} è su ${o.macchine_codici.join(", ")}. Confermi di spostarlo SOLO su ${newMacc} (perdi le altre assegnazioni)?`)) return;
        }
        const dest = ord.filter(x => (x.macchine_codici || []).includes(newMacc)).map(x => x.id);
        try {
            await api.post("/ordini-estrusione/reorder", { ids_in_order: [...dest, o.id], macchina_codice: newMacc });
            toast.success(`Spostato solo su ${newMacc}`); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const chiudi = async (o) => {
        const target = o.kg_target || 0;
        const done = o.kg_prodotti || 0;
        const diff = target > 0 ? Math.abs(done - target) / target : 0;
        const msg = diff > 0.15
            ? `⚠️ FUORI TOLLERANZA: prodotti ${done} kg vs target ${target} kg (${(diff*100).toFixed(1)}%). Chiudere lo stesso?`
            : `Chiudere ordine ${o.numero}?\nProdotti ${done} kg su ${target} kg target.`;
        if (!window.confirm(msg)) return;
        try {
            const { data } = await api.post(`/ordini-estrusione/${o.id}/chiudi`);
            if (data.fuori_tolleranza) toast.warning("Ordine chiuso — fuori tolleranza");
            else toast.success("Ordine chiuso");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const riapri = async (o) => {
        if (!window.confirm(`Riaprire ordine ${o.numero}?`)) return;
        try { await api.post(`/ordini-estrusione/${o.id}/riapri`); toast.success("Riaperto"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const parcheggia = async (o) => {
        if (!window.confirm(`Parcheggiare ordine ${o.numero}?\n\nL'ordine sparisce dalla coda di ${o.macchina_codice} e dagli operatori, ma resta modificabile e riprendibile in qualsiasi momento.`)) return;
        try { await api.post(`/ordini-estrusione/${o.id}/parcheggia`); toast.success(`Ordine ${o.numero} parcheggiato`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const riprendi = async (o, targetMacc) => {
        const dest = targetMacc || o.macchina_codice;
        if (!window.confirm(`Riprendere ordine ${o.numero} su ${dest}?`)) return;
        try {
            await api.post(`/ordini-estrusione/${o.id}/riprendi${targetMacc ? `?macchina_codice=${encodeURIComponent(targetMacc)}` : ""}`);
            toast.success(`Ripreso su ${dest}`); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.3em] font-mono text-amber-400/80 flex items-center gap-2">
                        <Factory size={12} /> LINEA ESTRUSIONE
                    </div>
                    <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Ordini Estrusione</h1>
                    <p className="mt-2 text-slate-400 text-sm max-w-2xl">Coda ordini per macchina. Trascina l'ordine o usa le frecce per riordinare.</p>
                </div>
                <div className="flex gap-3">
                    <Select value={stato} onValueChange={setStato}>
                        <SelectTrigger data-testid="select-stato-oe" className="bg-slate-900/60 border-slate-800 w-40"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                            <SelectItem value="aperto">Aperti</SelectItem>
                            <SelectItem value="chiuso">Chiusi</SelectItem>
                            <SelectItem value="tutti">Tutti</SelectItem>
                        </SelectContent>
                    </Select>
                    {canEdit && <Button onClick={openNew} data-testid="button-new-oe" className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                        <Plus size={16} /> Nuovo ordine
                    </Button>}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {macchineDaMostrare.map(m => (
                    <Card key={m.codice} className={`card p-4 ${m.virtual ? "border-2 border-dashed border-slate-700 bg-slate-950/40" : ""}`}>
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className={`font-display font-extrabold text-lg flex items-center gap-2 ${m.virtual ? "text-slate-300" : "text-amber-400"}`}>
                                    {m.virtual && <Pause size={16} className="text-slate-400"/>}
                                    {m.virtual ? "PARCHEGGIO" : m.codice}
                                </div>
                                <div className="text-[10px] text-slate-500 tracking-widest uppercase">{m.descrizione || "Estrusore"}</div>
                            </div>
                            <Badge className="badge-pill bg-slate-800 text-slate-300 border-slate-700">{(byMacc[m.codice] || []).length} ordini</Badge>
                        </div>
                        <div className="space-y-2">
                            {(byMacc[m.codice] || []).length === 0 && <div className="text-[11px] text-slate-500 py-4 text-center">{m.virtual ? "Nessun ordine sospeso" : "Nessun ordine"}</div>}
                            {(byMacc[m.codice] || []).map((o, idx, arr) => {
                                const prog = o.kg_target > 0 ? Math.min(1, (o.kg_prodotti || 0) / o.kg_target) : 0;
                                return (
                                    <div key={o.id} className={`p-3 rounded border ${o.stato === "chiuso" ? "bg-slate-800/30 border-slate-800" : (m.virtual ? "bg-slate-900/60 border-slate-700" : "bg-slate-900/40 border-slate-800")}`}
                                         data-testid={`row-oe-${o.numero}`}>
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="doc-code text-xs text-amber-300">{o.numero}
                                                    {m.virtual && <span className="ml-2 text-[10px] text-slate-500">era su {(o.macchine_codici || []).join("+") || o.macchina_codice}</span>}
                                                    {!m.virtual && (o.macchine_codici?.length > 1) && (
                                                        <span className="ml-2 text-[10px] text-cyan-400" title={`Ordine anche su: ${o.macchine_codici.filter(x => x !== m.codice).join(", ")}`}>
                                                            +{o.macchine_codici.filter(x => x !== m.codice).join(", ")}
                                                        </span>
                                                    )}
                                                    {o.fuori_tolleranza && <Badge className="ml-2 badge-pill bg-red-500/15 text-red-300 border-red-500/40 gap-1"><AlertTriangle size={10} /> fuori toll.</Badge>}
                                                    {o.stato === "chiuso" && <Badge className="ml-2 badge-pill bg-slate-700 text-slate-300">CHIUSO</Badge>}
                                                    {m.virtual && o.stato !== "chiuso" && <Badge className="ml-2 badge-pill bg-slate-800 text-slate-400 border-slate-700 text-[10px]"><Pause size={10} className="mr-1"/>Sospeso</Badge>}
                                                </div>
                                                <div className="text-xs text-slate-100 truncate">
                                                    <span className="font-mono text-emerald-300 mr-1">{o.articolo?.codice}</span>
                                                    {o.articolo?.tipo_estrusione} · {o.articolo?.larghezza_cm}cm · {o.articolo?.spessore_my}μ · {o.articolo?.colore_nome}
                                                </div>
                                                {o.materiale && <div className="text-[10px] text-slate-500 mt-0.5">MAT: {o.materiale}</div>}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {canEdit && o.stato === "aperto" && !m.virtual && (<>
                                                    <button onClick={()=>moveInMacc(o, "up", m.codice)} disabled={idx===0} className="p-1 rounded hover:bg-slate-700/50 text-slate-400 disabled:opacity-30" data-testid={`btn-oe-up-${o.numero}`}><ArrowUp size={14} /></button>
                                                    <button onClick={()=>moveInMacc(o, "down", m.codice)} disabled={idx===arr.length-1} className="p-1 rounded hover:bg-slate-700/50 text-slate-400 disabled:opacity-30" data-testid={`btn-oe-down-${o.numero}`}><ArrowDown size={14} /></button>
                                                </>)}
                                            </div>
                                        </div>
                                        <div className="mt-2 flex items-center gap-2">
                                            <div className="flex-1 h-1.5 bg-slate-800 rounded overflow-hidden">
                                                <div className={`h-full ${o.stato === "chiuso" ? "bg-slate-500" : "bg-emerald-400"}`} style={{width: `${prog*100}%`}} />
                                            </div>
                                            <div className="text-[10px] font-mono text-slate-400 whitespace-nowrap">{o.kg_prodotti || 0}/{o.kg_target} kg · {o.n_bobine} bob</div>
                                        </div>
                                        <div className="mt-2 flex flex-wrap items-center gap-1">
                                            <Button size="sm" variant="outline" onClick={()=>nav(`/estrusione/stampa/${o.id}`)}
                                                    className="h-7 px-2 bg-transparent border-slate-700 text-slate-300 gap-1" data-testid={`btn-oe-print-${o.numero}`}>
                                                <Printer size={12} /> A4
                                            </Button>
                                            {canEdit && o.stato === "aperto" && !m.virtual && (
                                                <Select value="" onValueChange={(v)=>moveToMacc(o, v)}>
                                                    <SelectTrigger className="h-7 w-28 bg-slate-900/40 border-slate-800 text-xs">solo su…</SelectTrigger>
                                                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                                        {macc.filter(mm => mm.codice !== m.codice).map(mm =>
                                                            <SelectItem key={mm.codice} value={mm.codice}>{mm.codice}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                            {canEdit && m.virtual && o.stato === "aperto" && (
                                                <Select value="" onValueChange={(v)=>riprendi(o, v)}>
                                                    <SelectTrigger className="h-7 flex-1 min-w-[7rem] bg-emerald-500/10 border-emerald-500/40 text-emerald-300 text-xs gap-1"
                                                                   data-testid={`btn-riprendi-${o.numero}`}>
                                                        <Play size={12} /> Riprendi su…
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                                        {macc.map(mm => <SelectItem key={mm.codice} value={mm.codice}>{mm.codice}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                            {canEdit && o.stato === "aperto" && <button onClick={()=>openEdit(o)} className="p-1 rounded hover:bg-slate-700/50 text-slate-300" data-testid={`btn-oe-edit-${o.numero}`}><Pencil size={12} /></button>}
                                            {canEdit && o.stato === "aperto" && !m.virtual && <Button size="sm" onClick={()=>parcheggia(o)} className="h-7 px-2 bg-slate-700/40 border border-slate-600 text-slate-300 gap-1 hover:bg-slate-700/60" data-testid={`btn-oe-parcheggia-${o.numero}`}><Pause size={12} /> Sospendi</Button>}
                                            {canClose && o.stato === "aperto" && <Button size="sm" onClick={()=>chiudi(o)} className="h-7 px-2 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 gap-1 hover:bg-emerald-500/30" data-testid={`btn-oe-chiudi-${o.numero}`}><Lock size={12} /> Chiudi</Button>}
                                            {canClose && o.stato === "chiuso" && <Button size="sm" onClick={()=>riapri(o)} className="h-7 px-2 bg-blue-500/20 border border-blue-500/40 text-blue-300 gap-1 hover:bg-blue-500/30" data-testid={`btn-oe-riapri-${o.numero}`}><Unlock size={12} /> Riapri</Button>}
                                            {canEdit && o.stato === "aperto" && o.n_bobine === 0 && <button onClick={()=>del(o)} className="p-1 rounded hover:bg-red-500/20 text-red-400" data-testid={`btn-oe-del-${o.numero}`}><Trash2 size={12} /></button>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                ))}
            </div>

            <Dialog open={openDlg} onOpenChange={setOpenDlg}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-4xl">
                    <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica ordine estrusione" : "Nuovo ordine estrusione"}</DialogTitle></DialogHeader>
                    <div className="space-y-3 max-h-[75vh] overflow-y-auto pr-2">
                        {/* Prima riga: macchine (multi) + articolo + kg — SCELTE PRINCIPALI */}
                        <div className="grid grid-cols-12 gap-2">
                            <div className="col-span-3">
                                <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Estrusori * <span className="normal-case tracking-normal text-amber-400">(uno o più)</span></Label>
                                <div className="flex flex-wrap gap-1 mt-1 p-2 bg-slate-950/50 border border-slate-800 rounded" data-testid="multi-macc-oe">
                                    {macc.map(m => {
                                        const sel = form.macchine_codici?.includes(m.codice);
                                        return (
                                            <button
                                                key={m.codice}
                                                type="button"
                                                data-testid={`toggle-macc-${m.codice}`}
                                                onClick={()=>toggleMacc(m.codice)}
                                                className={`px-2.5 py-1 rounded font-mono text-xs font-bold border transition ${sel ? "bg-amber-500 text-slate-950 border-amber-500" : "bg-slate-900 text-slate-400 border-slate-700 hover:border-slate-600"}`}
                                            >{m.codice}</button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="col-span-7">
                                <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Articolo * <span className="normal-case tracking-normal text-slate-400">(digita per cercare)</span></Label>
                                <Input data-testid="input-art-oe" list="art-estr-datalist"
                                       value={form.articolo_codice}
                                       onChange={e=>{
                                           const v = e.target.value;
                                           const match = artc.find(a => a.codice === v.toUpperCase().trim());
                                           setForm({...form, articolo_codice: v, articolo_id: match?.id || ""});
                                       }}
                                       placeholder="Cerca per codice o descrizione…"
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" />
                                <datalist id="art-estr-datalist">
                                    {artc.map(a => (
                                        <option key={a.id} value={a.codice}>
                                            {a.tipo_estrusione} {a.larghezza_cm}×{a.spessore_my}μ {a.colore_nome}{a.cliente_nome ? ` — ${a.cliente_nome}` : ""}
                                        </option>
                                    ))}
                                </datalist>
                            </div>
                            <div className="col-span-2">
                                <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Kg target *</Label>
                                <Input data-testid="input-kg-oe" type="number" value={form.kg_target}
                                       onChange={e=>setForm({...form, kg_target: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono text-lg font-bold" />
                            </div>
                        </div>

                        {/* Griglia ricetta: dati dell'articolo. TUBOLARE in normale (bianco), Colore/Misura/Spessore evidenziati. */}
                        {selArt ? (
                            <div className="border border-slate-800 rounded-lg overflow-hidden bg-slate-950/30">
                                <div className="px-3 py-1.5 bg-slate-900/60 text-[10px] uppercase tracking-widest text-amber-400/80">Ricetta dall'articolo</div>
                                <div className="grid grid-cols-4 gap-0 text-xs">
                                    <div className="p-2.5 border-t border-l border-slate-800">
                                        <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1">Cliente <span className="text-amber-400 normal-case tracking-normal">✎</span></div>
                                        <Input data-testid="input-cliente-oe" value={form.cliente_override ?? ""}
                                               onChange={e=>setForm({...form, cliente_override: e.target.value})}
                                               className="bg-slate-950/60 border-slate-800 mt-1 h-8 text-sm"
                                               placeholder={selArt.cliente_nome || "Cliente specifico o vuoto"} />
                                    </div>
                                    <ReadCell label="Tipo estrusione" value={selArt.tipo_estrusione} />
                                    <ReadCell label="Colore" value={`${selArt.colore_nome} (${selArt.colore_codice})`} strong />
                                    <ReadCell label="Codice" value={selArt.codice} mono />

                                    <ReadCell label="Misura cm" value={selArt.larghezza_cm} mono strong />
                                    <ReadCell label="Spessore μ" value={selArt.spessore_my} mono strong />
                                    <ReadCell label="PML (g)" value={selArt.pml_gr} mono />
                                    <ReadCell label="Trattamento" value={selArt.trattamento || "—"} />
                                </div>
                                <div className="grid grid-cols-2 gap-0 border-t border-slate-800">
                                    <div className="p-2.5 border-r border-slate-800">
                                        <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1">Stampa n° colori <span className="text-amber-400 normal-case tracking-normal">✎ modificabile</span></div>
                                        <Input data-testid="input-stampa-oe" value={form.stampa_colori_override ?? ""}
                                               onChange={e=>setForm({...form, stampa_colori_override: e.target.value})}
                                               className="bg-slate-950/60 border-slate-800 mt-1 h-8 text-sm"
                                               placeholder={selArt.stampa_colori || "0 · 1 · 2 · NO"} />
                                    </div>
                                    <div className="p-2.5">
                                        <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1">Additivi <span className="text-amber-400 normal-case tracking-normal">✎ modificabile</span></div>
                                        <Input data-testid="input-additivi-oe" value={form.additivi_override ?? ""}
                                               onChange={e=>setForm({...form, additivi_override: e.target.value})}
                                               className="bg-slate-950/60 border-slate-800 mt-1 h-8 text-sm"
                                               placeholder={selArt.additivi || "es. master UV 2%"} />
                                    </div>
                                </div>
                                <div className="px-3 py-1.5 border-t border-slate-800 bg-slate-950/40 text-[10px] text-slate-500">
                                    Modifiche a "Cliente", "Stampa n° colori" o "Additivi": al salvataggio l'app chiede se applicarle <b className="text-slate-300">solo a questo ordine</b> oppure <b className="text-slate-300">per sempre sull'articolo</b>.
                                </div>
                            </div>
                        ) : (
                            <div className="text-[11px] text-slate-500 italic border border-slate-800 border-dashed rounded p-3">Seleziona un articolo per vedere la ricetta.</div>
                        )}

                        {/* Campi overridabili sull'ordine */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Materiale (per stampa)</Label>
                                <Input data-testid="input-mat-oe" value={form.materiale}
                                       onChange={e=>setForm({...form, materiale: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1" placeholder="BD AMBRA" />
                            </div>
                            <div>
                                <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Note ordine</Label>
                                <Textarea data-testid="input-note-oe" rows={2} value={form.note}
                                          onChange={e=>setForm({...form, note: e.target.value})}
                                          className="bg-slate-950/50 border-slate-800 mt-1" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setOpenDlg(false)} className="bg-transparent border-slate-700 text-slate-300">Annulla</Button>
                        <Button onClick={save} data-testid="button-save-oe" className="bg-amber-500 hover:bg-amber-400 text-slate-950">{editId ? "Aggiorna" : "Crea"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ReadCell({ label, value, mono, strong, span = 1 }) {
    return (
        <div className={`p-2.5 border-t border-l border-slate-800 ${span === 2 ? "col-span-2" : ""}`}>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
            <div className={`mt-0.5 ${mono ? "font-mono" : ""} ${strong ? "font-bold text-amber-300" : "text-slate-100"} text-sm truncate`}>{value || "—"}</div>
        </div>
    );
}
